import React, { FC } from 'react';
import {
  ButtonProps,
  Grid,
  GridProps,
  IconButton,
  IconButtonProps,
  InputBaseComponentProps,
  Button as MuiButton,
  Table as MuiTable,
  TableCell as MuiTableCell,
  TableCellProps,
  TableProps,
  TextField,
  TextFieldProps,
  Typography,
  TypographyProps,
} from '@mui/material';
import { styled } from '@mui/material';
import MaskedInput, { MaskedInputProps } from 'react-text-mask';
import createAutoCorrectedDatePipe from 'text-mask-addons/dist/createAutoCorrectedDatePipe';
import { AddCardIcon } from '../assets/img';
import { IAddCardButtonProps, ICreditCardTextFieldProps } from '../types';

export const TableCell = styled(MuiTableCell)(({ theme }) => ({
  '&.MuiTableCell-body': {
    fontSize: 14,
    backgroundColor: theme.palette.common.white,
    borderBottomWidth: 2,
    borderBottomStyle: 'solid',
    borderBottomColor: theme.palette.grey[300],
    padding: 5,
    textAlign: 'right',
  },
})) as FC<TableCellProps>;

export const SelectedCell = styled(TableCell)(() => ({
  '&.MuiTableCell-body': {
    width: 40,
    paddingRight: 20,
    paddingLeft: 10,
  },
})) as FC<TableCellProps>;

export const TypeCell = styled(TableCell)(() => ({
  '&.MuiTableCell-body': {
    width: 40,
    padding: 2,
  },
})) as FC<TableCellProps>;

export const ActionsCell = styled(TableCell)(() => ({
  '&.MuiTableCell-body': {
    width: 60,
    padding: 0,
    textAlign: 'right',
  },
})) as FC<TableCellProps>;

export const ActionsButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey[300],
  padding: 8,
  '&:hover': {
    color: theme.palette.primary.main,
  },
})) as FC<IconButtonProps>;

export const RemoveActionsButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.grey[300],
  padding: 8,
  '&:hover': {
    color: theme.palette.error.main,
  },
})) as FC<IconButtonProps>;

export const Table = styled(MuiTable)(() => ({
  borderCollapse: 'separate',
  width: '100%',
  marginTop: 20,
})) as FC<TableProps>;

const Button = styled(MuiButton)(() => ({
  borderRadius: 20,
  fontSize: 14,
  textTransform: 'uppercase',
})) as FC<ButtonProps>;

const AddCardButtonContainer = styled(Grid)(() => ({
  marginTop: 20,
})) as FC<GridProps>;

export const AddCardButton: FC<IAddCardButtonProps> = ({ label, onAdd }) => (
  <AddCardButtonContainer container justifyContent="flex-end">
    <Button variant="outlined" color="primary" startIcon={<AddCardIcon />} onClick={onAdd}>
      {label}
    </Button>
  </AddCardButtonContainer>
);

const CreditCardTextFieldBase = styled(TextField)(() => ({
  marginBottom: 20,
})) as FC<TextFieldProps>;

export const CreditCardBrandTextField = styled(Typography)(() => ({
  textTransform: 'uppercase',
})) as FC<TypographyProps>;

export const CreditCardTextField: FC<ICreditCardTextFieldProps & TextFieldProps> = props => (
  <CreditCardTextFieldBase
    variant="outlined"
    InputLabelProps={{
      shrink: true,
    }}
    {...props}
  />
);

const CreditCardExpDateTextMask: FC<InputBaseComponentProps> = props => {
  const { inputRef, ...other } = props;

  return (
    <MaskedInput
      {...(other as MaskedInputProps)}
      ref={ref => {
        inputRef(ref ? ref.inputElement : null);
      }}
      mask={[/\d/, /\d/, '/', /\d/, /\d/]}
      showMask
      guide
      keepCharPositions
      pipe={createAutoCorrectedDatePipe('mm/yy', {
        minYear: 1900,
      })}
    />
  );
};

export const CreditCardExpDateTextField: FC<ICreditCardTextFieldProps & TextFieldProps> = props => (
  <CreditCardTextField {...props} InputProps={{ inputComponent: CreditCardExpDateTextMask }} />
);

export const FormContentContainer = styled(Grid)(() => ({
  padding: 10,
}));

export const CreditCardManagementContainer = styled(Grid)(() => ({
  padding: 30,
})) as FC<GridProps>;

export const RenewalContainer: FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    style={{
      marginBottom: 30,
      border: '1px solid #e0e0e0',
      borderRadius: 5,
      paddingRight: 15,
      paddingTop: 10,
      paddingBottom: 10,
    }}>
    {children}
  </div>
);
